import { useCallback, useEffect, useMemo, useState } from "react";
import { IconButton, Stack, Popover, Card, OptionList } from "@introist/react-foundation/v2";
import { SquareCheck } from "lucide-react";
import { Editor } from "@tiptap/react";
import { api } from "services/rpc/RpcProvider";
import { useLocalStorage } from "usehooks-ts";
import { TourCard } from "components/organisms";

export const TaskLinkSelector = ({ editor }: { editor?: Editor | null }) => {
  const [popoverRef, setPopoverRef] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useLocalStorage(
    "intr.htmlEditor.hasSeenTaskLinkTutorial",
    false
  );
  const [showTutorial, setShowTutorial] = useState(false);

  const { data: tasks } = api.tasks.list.useQuery({});

  // Show tutorial on first editor open
  useEffect(() => {
    if (!hasSeenTutorial && popoverRef) {
      const timer = setTimeout(() => {
        setShowTutorial(true);
        setHasSeenTutorial(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [hasSeenTutorial, setHasSeenTutorial, popoverRef]);

  const handleTaskSelect = useCallback(
    (taskId: string, taskTitle: string) => {
      if (!editor) return;

      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      const taskUrl = `{{task.${taskId}.completeUrl}}`;

      if (hasSelection) {
        // User has selected text - convert it to a link
        editor.chain().focus().extendMarkRange("link").setLink({ href: taskUrl }).run();
      } else {
        // No selection - insert link with task title as text
        editor.chain().focus().insertContent(`<a href="${taskUrl}">${taskTitle}</a>`).run();
      }

      setOpen(false);
    },
    [editor]
  );

  const options = useMemo(() => {
    return (tasks ?? []).map(task => ({
      key: task.id,
      title: task.title,
      onClick: () => handleTaskSelect(task.id, task.title)
    }));
  }, [tasks, handleTaskSelect]);

  const handleCloseTutorial = useCallback(() => {
    setShowTutorial(false);
  }, []);

  return (
    <>
      <Stack ref={setPopoverRef}>
        <IconButton icon={<SquareCheck size={16} />} onClick={() => setOpen(!open)} size="large" />
      </Stack>
      <TourCard
        referenceElement={popoverRef}
        open={showTutorial}
        onClose={handleCloseTutorial}
        placement="bottom"
        title={
          <TourCard.IconTitle icon="checkRectangle" color="var(--palette-primary-default)">
            Task Links
          </TourCard.IconTitle>
        }
        closeButtonText="Got it!"
      >
        <Stack vertical gap="small">
          <div>Use this button to insert task completion links in your messages.</div>
          <div>
            <strong>How it works:</strong>
          </div>
          <ul style={{ margin: "0 0 0 var(--spacing-medium)", padding: 0 }}>
            <li>Select text or click the button</li>
            <li>Choose a task from your workspace</li>
            <li>A completion link will be inserted automatically</li>
          </ul>
          <div style={{ fontSize: "var(--font-size-small)", opacity: 0.8 }}>
            When the recipient clicks the link, the task is marked as complete.
          </div>
        </Stack>
      </TourCard>
      <Popover
        referenceElement={popoverRef}
        open={open}
        onClose={() => setOpen(false)}
        closeOnContentClick={false}
      >
        <Card elevated style={{ padding: 0, minWidth: "300px" }}>
          <OptionList searchable options={options} placeholder="Search tasks..." />
        </Card>
      </Popover>
    </>
  );
};
